import React from "react";
import { Button, Modal, Upload, UploadProps, Typography, Alert } from "antd";
import {
  UploadOutlined,
  FilePdfOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useState, useEffect } from "react";
import { openNotification } from "../../../../Components/Notification";

interface ResumeUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  handleUpload: (file: any) => void;
  uploadError?: {
    headline: string;
    body: string;
    guidance?: string[];
    isRetryable: boolean;
  } | null;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const ResumeUploadModal: React.FC<ResumeUploadModalProps> = ({
  isOpen,
  onClose,
  handleUpload,
  uploadError,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
    }
  }, [isOpen]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const beforeUpload = (file: File): boolean => {
    if (!["application/pdf"].includes(file.type)) {
      openNotification(
        "error",
        "Invalid file type",
        "Only PDF files are allowed"
      );
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      openNotification(
        "error",
        "File size exceeded",
        "File size cannot exceed 5 MB"
      );
      return false;
    }

    setSelectedFile(file);

    return false;
  };

  const handleFileChange = (info: any) => {
    const { file } = info;

    if (file.originFileObj) {
      const fileObj = file.originFileObj;
      beforeUpload(fileObj);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      beforeUpload(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  const handleUploadClick = () => {
    if (selectedFile) {
      handleUpload(selectedFile);
    }
  };

  const props: UploadProps = {
    name: "file",
    multiple: false,
    showUploadList: false,
    accept: ".pdf",
    beforeUpload,
    onChange: handleFileChange,
    onDrop: handleDrop,
    disabled: selectedFile ? true : false,
  };

  return (
    <Modal
      title="Upload Resume"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      maskClosable={false}
      destroyOnClose
    >
      <section className="resume-upload-modal-content">
        {uploadError && (
          <Alert
            type="error"
            showIcon
            message={uploadError.headline}
            description={
              <div>
                <p style={{ margin: '8px 0 0 0' }}>{uploadError.body}</p>
                {uploadError.guidance && uploadError.guidance.length > 0 && (
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px' }}>
                    {uploadError.guidance.map((item, idx) => (
                      <li key={idx} style={{ marginBottom: '4px' }}>
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            }
            style={{ marginBottom: '16px' }}
          />
        )}
        <Upload.Dragger {...props}>
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Upload PDF files only. Maximum file size allowed is 5 MB.
          </p>
        </Upload.Dragger>

        {selectedFile && (
          <div className="resume-upload-preview">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: 12,
                backgroundColor: "#f0f2f5",
                borderRadius: 4,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FilePdfOutlined style={{ fontSize: 24, color: "#ff4d4f" }} />
                <div>
                  <Typography.Text strong>{selectedFile.name}</Typography.Text>
                  <br />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {formatFileSize(selectedFile.size)}
                  </Typography.Text>
                </div>
              </div>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={handleRemoveFile}
              >
                Remove
              </Button>
            </div>
          </div>
        )}

        <div className="resume-upload-modal-footer">
          <Button type="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleUploadClick}
            disabled={!selectedFile}
          >
            Upload Resume
          </Button>
        </div>
      </section>
    </Modal>
  );
};

export default ResumeUploadModal;
